import { AsYouType } from 'libphonenumber-js';
import React from 'react';
import { AvatarInputValue } from 'types/avatarInputTypes';

import {
  PartnerProfileFormValues,
  PartnerProfileFormViolation,
} from 'types/partnerProfileFormTypes';
import { ImageSizeViolationError } from 'utils/errors';
import { validatePartnerProfileAvatarInputFile } from 'utils/validatePartnerProfileAvatarFile';
import {
  createPartnerProfileFormViolationLense,
  validatePartnerProfileFormValuesImmediately,
} from 'utils/validatePartnerProfileFormValues';
import { useSafeAsyncWrapper } from './useSafeAsyncWrapper';

const initialValues: PartnerProfileFormValues = {
  avatar: null,
  firstName: '',
  lastName: '',
  middleName: '',
  phone: '',
  country: null,
  city: '',
  details: '',
};

export const usePartnerProfileForm = () => {
  const [values, setValues] = React.useState<PartnerProfileFormValues>(
    initialValues
  );

  const [violations, setViolations] = React.useState<
    PartnerProfileFormViolation[]
  >([]);

  React.useEffect(() => {
    setViolations(validatePartnerProfileFormValuesImmediately(values));
  }, [values]);

  const rawAvatarFile = values.avatar?.rawFile;

  const wrapAsyncSafely = useSafeAsyncWrapper();

  React.useEffect(() => {
    if (rawAvatarFile) {
      const promise = validatePartnerProfileAvatarInputFile(
        rawAvatarFile
      ).catch((error) => {
        if (error instanceof ImageSizeViolationError) {
          setViolations((prev) => [...prev, { name: 'AvatarInputImageSize' }]);
        }
      });

      wrapAsyncSafely(promise);
    }
  }, [rawAvatarFile, values, wrapAsyncSafely]);

  const setFirstName = React.useCallback((nextValue: string) => {
    setValues((prevValues) => ({
      ...prevValues,
      firstName: nextValue,
    }));

    setViolations((prev) =>
      prev.filter(
        (violation) =>
          !createPartnerProfileFormViolationLense(
            violation
          ).isFirstNameViolation()
      )
    );
  }, []);

  const setMiddleName = React.useCallback((nextValue: string) => {
    setValues((prevValues) => ({
      ...prevValues,
      middleName: nextValue,
    }));

    setViolations((prev) =>
      prev.filter(
        (violation) =>
          !createPartnerProfileFormViolationLense(
            violation
          ).isMiddleNameViolation()
      )
    );
  }, []);

  const setLastName = React.useCallback((nextValue: string) => {
    setValues((prevValues) => ({
      ...prevValues,
      lastName: nextValue,
    }));

    setViolations((prev) =>
      prev.filter(
        (violation) =>
          !createPartnerProfileFormViolationLense(
            violation
          ).isLastNameViolation()
      )
    );
  }, []);

  const setPhone = React.useCallback((nextValue: string) => {
    const nextValueFormatted = new AsYouType().input(nextValue);

    setValues((prevValues) => ({
      ...prevValues,
      phone: nextValueFormatted,
    }));

    setViolations((prev) =>
      prev.filter(
        (violation) =>
          !createPartnerProfileFormViolationLense(violation).isPhoneViolation()
      )
    );
  }, []);

  const setCountry = React.useCallback((nextValue: string) => {
    setValues((prevValues) => ({
      ...prevValues,
      country: nextValue,
    }));

    setViolations((prev) =>
      prev.filter(
        (violation) =>
          !createPartnerProfileFormViolationLense(
            violation
          ).isCountryViolation()
      )
    );
  }, []);

  const setCity = React.useCallback((nextValue: string) => {
    setValues((prevValues) => ({
      ...prevValues,
      city: nextValue,
    }));

    setViolations((prev) =>
      prev.filter(
        (violation) =>
          !createPartnerProfileFormViolationLense(violation).isCityViolation()
      )
    );
  }, []);

  const setAvatar = React.useCallback((nextValue: AvatarInputValue) => {
    setValues((prevValues) => ({
      ...prevValues,
      avatar: nextValue,
    }));

    setViolations((prev) =>
      prev.filter(
        (violation) =>
          !createPartnerProfileFormViolationLense(violation).isAvatarViolation()
      )
    );
  }, []);

  const setDetails = React.useCallback((nextValue: string) => {
    setValues((prevValues) => ({
      ...prevValues,
      details: nextValue,
    }));

    setViolations((prev) =>
      prev.filter(
        (violation) =>
          !createPartnerProfileFormViolationLense(
            violation
          ).isDetailsViolation()
      )
    );
  }, []);

  return {
    values,
    violations,
    setValues,
    setViolations,
    setFirstName,
    setLastName,
    setMiddleName,
    setPhone,
    setCountry,
    setCity,
    setAvatar,
    setDetails,
  };
};
